import { ChangeEvent, useCallback, useEffect, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';

import { errorNormalizer, validateName } from '../../../shared/Helpers/functions';
import { CustomInput, CustomSelect, MultipleSelect, Text } from '../../../shared/uiComponents';
import { useResetCalendarData } from '../../Calendar/Helpers/hooks';

import { SignatureInput, SoapNoteButtons, TimeInfo } from '../components';
import { usePostSubmitEditMode, useSignatureDate, useSoapNoteFieldDisabled } from '../helpers';
import {
	ACTIONS,
	ERRORS,
	MeetAndGreetErrorReducer,
	initialMeetAndGreetErrorState,
	initialMeetAndGreetState,
	meetAndGreetReducer,
} from './reducer';
import { useMeetAndGreetOptions } from '../hook';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { setDate, setEndTime, setStartTime } from '../../../redux/State/clientSlice/sessionSlice';
import { getSoapNote, updateMeetAndGreet } from '../../../redux/State/clientSlice/soapNoteSlice';
import {
	MeetAndGreetProperties,
	UpdateMeetAndGreetProperties,
} from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const MeetAndGreet = () => {
	const signatureDate = useSignatureDate();
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { resetData } = useResetCalendarData();
	const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
	const isMounted = useRef(true);

	const { barriersList, participantsList, goalAddressedList } = useMeetAndGreetOptions();

	const reportId = params.get('reportId');
	const clientId = params.get('clientId');
	const forcedOperation = params.get('forcedOperation') === 'true';

	const { user } = useSelector((state) => state.session.sessionInfo);
	const sessionInfo = useSelector((state) => state.session.sessionInfo);
	const locationList = useSelector((state) => state.soapNote.locations);
	const errorList = useSelector((state) => state.soapNote.error?.errors);

	const [data, setData] = useReducer(meetAndGreetReducer, initialMeetAndGreetState);

	const { isPostSubmitEditMode } = usePostSubmitEditMode();
	const { getFieldDisabled } = useSoapNoteFieldDisabled();

	const [errors, setErrors] = useReducer(MeetAndGreetErrorReducer, initialMeetAndGreetErrorState);

	useEffect(() => {
		if (data.participantResponses.length) return;
		const payload = participantsList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setParticipants, payload });
	}, [participantsList, data, setData]);

	useEffect(() => {
		if (!errorList) return;
		const list = Object.keys(errorList);
		list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
	}, [errorList]);

	useEffect(() => {
		isMounted.current = true;

		return () => {
			isMounted.current = false;

			if (currentUpdateRequestRef.current) {
				console.info('Aborting request on unmount');
				currentUpdateRequestRef.current.abort();
			}
		};
	}, []);

	useEffect(() => {
		if (!reportId || !clientId) return;

		dispatch(getSoapNote({ reportId, clientId }))
			.then(unwrapResult)
			.then((response) => {
				const { location, date, startTime, endTime, participantResponses, barrierToTreatmentResponses } =
					response as MeetAndGreetProperties;
				const barriers = barrierToTreatmentResponses?.map((x) => x.id);
				const participants = participantResponses?.map((x) => x.id);

				dispatch(setDate(date));
				dispatch(setStartTime(startTime));
				dispatch(setEndTime(endTime));

				setData({
					type: ACTIONS.setState,
					payload: response,
				});

				setData({
					type: ACTIONS.setBarriers,
					payload: barriers,
				});

				setData({
					type: ACTIONS.setParticipants,
					payload: participants,
				});

				if (location) {
					setData({
						type: ACTIONS.setLocationId,
						payload: location.id,
					});
				}
			})
			.catch((error) => {
				errorNormalizer(error);
				if (error.status === 403) navigate('/dashboard');
			});
	}, [dispatch, clientId, reportId, navigate]);

	const updateSoapNoteHandler = useCallback(
		(submit: boolean) => {
			if (currentUpdateRequestRef.current) {
				console.info('Aborting previous pending request');
				currentUpdateRequestRef.current.abort();
			}
			if (!reportId || !clientId || !data) return;
			const { date, startTime, endTime } = sessionInfo;
			const allData: UpdateMeetAndGreetProperties = {
				...data,
				date,
				startTime,
				endTime,
				reportId,
				submit,
				forcedOperation,
				clientId,
			};

			const requestPromise = dispatch(updateMeetAndGreet(allData));
			currentUpdateRequestRef.current = requestPromise;

			requestPromise
				.then(unwrapResult)
				.then(() => {
					if (!submit || !isMounted.current) return;
					resetData();
					toast('Success');
					navigate('/calendar');
				})
				.catch(errorNormalizer)
				.finally(() => {
					if (currentUpdateRequestRef.current === requestPromise) {
						currentUpdateRequestRef.current = null;
					}
				});
		},
		[reportId, clientId, forcedOperation, data, sessionInfo, navigate, resetData, dispatch],
	);

	const locationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
		if (!errors.LocationId) return;
		setErrors({ payload: false, type: ERRORS.LocationId });
	};

	const barriersHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setBarriers });
		if (!errors.BarriersResponses) return;
		setErrors({ payload: false, type: ERRORS.BarriersResponses });
	};

	// const goalAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
	//   const { value } = event.target;
	//   setData({ payload: value, type: ACTIONS.setGoalsAddressed });
	//   if (!errors.GoalsAddressed) return;
	//   setErrors({ payload: false, type: ERRORS.GoalsAddressed });
	// };

	const participantsHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setParticipants, payload: value });
		if (!errors.ParticipantResponses) return;
		setErrors({ payload: false, type: ERRORS.ParticipantResponses });
	};

	const sessionSummaryHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setSessionSummary });
		if (!errors.SessionSummary) return;
		setErrors({ payload: false, type: ERRORS.SessionSummary });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
		if (!errors.Signature) return;
		setErrors({ payload: false, type: ERRORS.Signature });
	};

	const barriersOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setBarriersOther });
		if (!errors.BarriersOther) return;
		setErrors({ payload: false, type: ERRORS.BarriersOther });
	};

	const participantOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setMeetAndGreetParticipantOther });
		if (!errors.MeetAndGreetParticipantOther) return;
		setErrors({ payload: false, type: ERRORS.ParticipantOther });
	};
	const saveHandler = useCallback(() => {
		if (isPostSubmitEditMode) return;
		updateSoapNoteHandler(false);
	}, [updateSoapNoteHandler, isPostSubmitEditMode]);

	const submitHandler = useCallback(() => updateSoapNoteHandler(true), [updateSoapNoteHandler]);

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			saveHandler();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const isOtherSelectedForBarriersToTreatment = data.barrierToTreatmentResponses?.find(
		(x) => barriersList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForParticipants = data.participantResponses?.find(
		(x) => participantsList.find((y) => y.id === x)?.text === 'Other',
	);

	return (
		<>
			<TimeInfo disabled={getFieldDisabled('startTime')} save={saveHandler} />
			<CustomInput label="Behavior Technician:" value={user.fullName} className="marginBottom16" disabled />
			<CustomSelect
				label="Location:"
				data={locationList}
				value={data.locationId ? data.locationId : ''}
				setValue={locationHandler}
				className="marginBottom16"
				disabled={getFieldDisabled('locationId')}
				onBlur={saveHandler}
				error={errors.LocationId}
			/>
			<MultipleSelect
				label="Session Participants:"
				data={participantsList}
				initialValue={data.participantResponses}
				setValue={participantsHandler}
				error={errors.ParticipantResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('participantResponses')}
				onBlur={saveHandler}
			/>
			{isOtherSelectedForParticipants && (
				<CustomInput
					label="If other, please describe: "
					value={data.meetAndGreetParticipantOther}
					onChange={participantOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('participantOther')}
					onBlur={saveHandler}
					error={errors.MeetAndGreetParticipantOther}
				/>
			)}
			<MultipleSelect
				label="Goals Addressed:"
				data={goalAddressedList}
				setValue={() => null}
				className="marginBottom16"
				initialValue={goalAddressedList.map((x) => x.id)}
				disabled
			/>
			<MultipleSelect
				label="Barriers to Treatment:"
				data={barriersList}
				setValue={barriersHandler}
				className="marginBottom16"
				initialValue={data.barrierToTreatmentResponses}
				disabled={getFieldDisabled('barrierToTreatmentResponses')}
				onBlur={saveHandler}
			/>
			{isOtherSelectedForBarriersToTreatment && (
				<CustomInput
					label="If other, please describe: "
					value={data.barrierToTreatmentOther}
					onChange={barriersOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('barrierToTreatmentOther')}
					onBlur={saveHandler}
					error={errors.BarriersOther}
				/>
			)}
			<CustomInput
				label="Session Summary: "
				value={data.sessionSummary}
				onChange={sessionSummaryHandler}
				className="marginBottom16"
				multiline
				disabled={getFieldDisabled('sessionSummary')}
				onBlur={saveHandler}
			/>
			<SignatureInput
				error={errors.Signature}
				value={data.signature}
				setValue={signatureHandler}
				disabled={getFieldDisabled('signature')}
				onBlur={validateSignatureHandler}
			/>
			<Text title={`Date/time of signature: ${signatureDate}`} fontWeight="semiBold" />
			<SoapNoteButtons save={saveHandler} submit={submitHandler} />
		</>
	);
};

export default MeetAndGreet;
